import api from 'api/api';
import serverCall from 'api/serverCall';
import { createContext, useReducer, ReactElement, useContext, useEffect, useCallback, useState } from 'react';
import {
  ADD_NOTIFICATION,
  SET_COUNTRY,
  SET_IS_ADVANCED,
  SET_SELECTED_USERS,
  SET_STOCKS,
  SET_STOCK_GROUP,
  SET_STRATEGIES
} from 'store/reducers/actions';
import dataReducer, { initialState } from 'store/reducers/data';
import { Country, DataContextType } from 'types/custom/data';
import { IStock, StockGroup } from 'types/custom/stock';
import { Notification } from 'types/custom/notification';
import { IUser } from 'types/custom/user';
import { useFirebaseContext } from './FirebaseContext';
import { IStrategy } from 'types/custom/strategy';

const DataContext = createContext<DataContextType | null>(null);

export const DataContextProvider = ({ children }: { children: ReactElement }) => {
  const { isLoggedIn, user } = useFirebaseContext();
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const [isStockGroupFetching, setIsStockGroupFetching] = useState<boolean>(false);
  const fetchAllStocks = useCallback(() => {
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllAvailableStocks(), (response, error) => {
        if (error) {
          reject(error);
        } else {
          dispatch({
            type: SET_STOCKS,
            payload: {
              stocks: response as IStock[]
            }
          });
          resolve(response);
        }
      });
    });
  }, []);

  const fetchAllNotifications = useCallback(() => {
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllNotifications(), (response, error) => {
        if (error) {
          reject(error);
        } else {
          dispatch({
            type: ADD_NOTIFICATION,
            payload: {
              notifications: response
            }
          });
          resolve(response);
        }
      });
    });
  }, []);

  const fetchAllStockGroups = (): Promise<StockGroup[]> => {
    setIsStockGroupFetching(true);
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllStockGroups(), (response, error) => {
        if (error) {
          setIsStockGroupFetching(false);
          reject(error);
        } else {
          dispatch({
            type: SET_STOCK_GROUP,
            payload: {
              stockGroups: response as StockGroup[]
            }
          });
          resolve(response);
          setIsStockGroupFetching(false);
        }
      });
    });
  };

  const fetchAllStrategies = (): Promise<IStrategy[]> => {
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllStrategies(), (response, error) => {
        if (error) {
          reject(error);
        } else {
          resolve(response);
        }
      });
    });
  };

  const fetchData = async () => {
    try {
      // Create an array of promises
      const promiseArray = [fetchAllStocks(), fetchAllNotifications(), fetchAllStockGroups()];

      // Conditionally add strategies fetching based on permissions
      if (user?.permissions.includes('canStartBacktests') || user?.permissions.includes('canStartLiveTrade')) {
        promiseArray.push(fetchAllStrategies());
      }

      // Await all promises and handle their statuses
      const results = await Promise.allSettled(promiseArray);

      // Destructure results with proper typing
      //@ts-ignore
      const [stocksResult, notificationResult, stockGroupResult, strategyResult] = results as [
        PromiseSettledResult<IStock[]>,
        PromiseSettledResult<Notification[]>,
        PromiseSettledResult<StockGroup[]>,
        PromiseSettledResult<IStrategy[]>
      ];

      // Handle each result based on its status
      // if (stocksResult.status === 'fulfilled') {
      //   dispatch({
      //     type: SET_STOCKS,
      //     payload: { stocks: stocksResult.value },
      //   });
      // } else {
      //   console.error('Failed to fetch stocks:', stocksResult.reason);
      // }

      if (notificationResult.status === 'fulfilled') {
        dispatch({
          type: ADD_NOTIFICATION,
          payload: { notifications: notificationResult.value }
        });
      } else {
        console.error('Failed to fetch notifications:', notificationResult.reason);
      }

      // if (stockGroupResult.status === 'fulfilled') {
      //   dispatch({
      //     type: SET_STOCK_GROUP,
      //     payload: { stockGroups: stockGroupResult.value },
      //   });
      // } else {
      //   console.error('Failed to fetch stock groups:', stockGroupResult.reason);
      // }

      if (strategyResult?.status === 'fulfilled') {
        dispatch({
          type: SET_STRATEGIES,
          payload: { strategies: strategyResult.value }
        });
      } else if (strategyResult) {
        console.error('Failed to fetch strategies:', strategyResult.reason);
      }
    } catch (error) {
      console.error('An error occurred while fetching data:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  // const setKiteConnectStatus = (status: boolean) => {
  //   dispatch({
  //     type: SET_KITE_CONNECT_STATUS,
  //     payload: {
  //       nnectStatus: status
  //     }
  //   });
  // };

  const setSelectedUsersToStore = (users: IUser[]) => {
    dispatch({
      type: SET_SELECTED_USERS,
      payload: {
        selectedUsers: users
      }
    });
  };

  const setAdvanced = (advanced: boolean) => {
    dispatch({
      type: SET_IS_ADVANCED,
      payload: {
        advanced: advanced
      }
    });
  };

  useEffect(() => {
    if (state.isAdvanced) {
      localStorage.setItem('advanced', 'true');
    } else {
      localStorage.removeItem('advanced');
    }
  }, [state.isAdvanced]);

  useEffect(() => {
    if (state.country) {
      localStorage.setItem('country', state.country.toString());
    }
  }, [state.country]);

  const setCountry = (country: Country) => {
    dispatch({
      type: SET_COUNTRY,
      payload: {
        country
      }
    });
  };

  return (
    <DataContext.Provider
      value={{
        ...state,
        setAdvanced,
        setCountry,
        isPrivacyProtectionActivated: localStorage.getItem('isPrivacyProtectionActivated') ? true : false,
        setSelectedUsersToStore,
        fetchAllStockGroups,
        isStockGroupFetching,
        fetchAllStocks,
        fetchAllNotifications
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a OrderContextProvider');
  }
  return context;
};
export default DataContext;
